import { Fragment } from 'react';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { type ChatViewer } from '@apps/web/hooks/use_phoebe_chat_presence';
import { cn } from '@apps/web/lib/utils';

function viewerLabel(viewer: ChatViewer, currentUserId: string | null): string {
    return viewer.userId === currentUserId
        ? `${viewer.displayName} (you)`
        : viewer.displayName;
}

function AvatarCircles({
    viewers,
    maximumVisible,
    currentUserId,
}: {
    readonly viewers: ReadonlyArray<ChatViewer>;
    readonly maximumVisible: number;
    readonly currentUserId: string | null;
}) {
    const hasOverflow = viewers.length > maximumVisible;
    const visibleCount = hasOverflow ? maximumVisible - 1 : maximumVisible;
    const visibleViewers = viewers.slice(0, visibleCount);
    const overflowCount = viewers.length - visibleViewers.length;

    return (
        <Fragment>
            {visibleViewers.map((viewer) => {
                const label = viewerLabel(viewer, currentUserId);
                return (
                    <Tooltip key={viewer.userId} delayDuration={200}>
                        <TooltipTrigger asChild={true}>
                            <span
                                tabIndex={0}
                                aria-label={label}
                                className="focus-visible:ring-ring shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                            >
                                <UserAvatar
                                    className="ring-background size-6 ring-2"
                                    colorKey={viewer.userId}
                                    displayName={viewer.displayName}
                                />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{label}</TooltipContent>
                    </Tooltip>
                );
            })}
            {overflowCount > 0 ? (
                <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild={true}>
                        <span
                            tabIndex={0}
                            aria-label={`${overflowCount} more: ${viewers
                                .slice(visibleCount)
                                .map((viewer) =>
                                    viewerLabel(viewer, currentUserId),
                                )
                                .join(', ')}`}
                            className="bg-muted text-muted-foreground ring-background focus-visible:ring-ring flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium ring-2 outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        >
                            +{overflowCount}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        {viewers
                            .slice(visibleCount)
                            .map((viewer) => viewerLabel(viewer, currentUserId))
                            .join(', ')}
                    </TooltipContent>
                </Tooltip>
            ) : null}
        </Fragment>
    );
}

export function ChatViewerPresence({
    viewers,
    currentUserId,
    className,
}: {
    readonly viewers: ReadonlyArray<ChatViewer>;
    readonly currentUserId: string | null;
    readonly className?: string;
}) {
    if (!viewers.some((viewer) => viewer.userId !== currentUserId)) {
        return null;
    }

    const names = viewers.map((viewer) => viewerLabel(viewer, currentUserId));
    const accessibleLabel = `${viewers.length} ${
        viewers.length === 1 ? 'person' : 'people'
    } viewing: ${names.join(', ')}`;

    return (
        <span
            role="group"
            aria-label={accessibleLabel}
            className={cn('flex min-w-0 shrink-0 items-center', className)}
        >
            <span className="flex items-center -space-x-1.5 @max-[420px]/chat-header:hidden">
                <AvatarCircles
                    viewers={viewers}
                    maximumVisible={3}
                    currentUserId={currentUserId}
                />
            </span>
            <span className="hidden items-center -space-x-1.5 @max-[420px]/chat-header:flex">
                <AvatarCircles
                    viewers={viewers}
                    maximumVisible={2}
                    currentUserId={currentUserId}
                />
            </span>
        </span>
    );
}
